/**
 * `POST /api/v2/ws/ticket/` connection descriptor (ADR-078 Amendment 1, 6-F),
 * shared by every web client that exchanges its JWT for a socket ticket.
 *
 * The response says where to connect, which transports to try after it, and
 * carries the single-use ticket that authorizes one connect. The descriptor
 * type is the generated schema's, so a client and
 * `WebSocketConnectionDescriptorSerializer` cannot drift.
 *
 * Only `ticket` is guaranteed. A server without the descriptor answers
 * `{"ticket"}` alone, so an absent `url` means "use the configured endpoint"
 * and an absent `expires_in` means "no connect deadline" — never a client-side
 * default. `liveness_interval_s` and `client_control_frames` are deliberately
 * unread: browsers cannot observe control frames, so web has no client-side
 * liveness detector.
 *
 * URL convention, shared with the server: `url` and every `fallback[].url` is
 * the full socket endpoint including its path and never carries a query
 * string. `socketUrlWithTicket` is the only place a credential enters a socket
 * URL, and the credential is always the ticket, never the JWT.
 *
 * Which hosts a build trusts and where a refusal is reported are each app's
 * decisions, passed in as `SocketConnectionPlanOptions`.
 */
import type { components } from './types';

/** The `POST /api/v2/ws/ticket/` response body. */
export type WebSocketConnectionDescriptor =
  components['schemas']['WebSocketConnectionDescriptor'];

/** Path `EverythingConsumer` is served on, for a configured socket host. */
export const WS_SOCKET_PATH = '/api/v2/ws/';

/** Query parameter the ticket travels in; a descriptor url never carries it. */
const TICKET_QUERY_PARAMETER = 'ticket';

const SOCKET_SCHEMES = ['ws:', 'wss:'];

/** Why a descriptor endpoint was refused. Reported without the ticket. */
export type SocketEndpointRejection =
  'not-allowlisted' | 'unsupported-scheme' | 'query-string' | 'malformed';

/** Receives each refused endpoint's reason and host, never its URL. */
export type SocketEndpointRejectionReporter = (
  reason: SocketEndpointRejection,
  host: string | null,
) => void;

/** What one connect needs: the ticket, its lifetime, and where to try. */
export interface SocketConnectionPlan {
  /** Single-use ticket; spent by the first connect that uses it. */
  ticket: string;
  /** Ticket lifetime, or null when the server advertised none. */
  ticketLifetimeMs: number | null;
  /** Primary first, then the allowlisted fallbacks in server order. */
  endpoints: string[];
}

export interface SocketConnectionPlanOptions {
  /** Hosts (lower-case, no port) this build accepts a socket connection to. */
  allowlist: readonly string[];
  /**
   * The build's own socket endpoint, used when no advertised endpoint
   * survives; null when the build has none.
   */
  configuredEndpoint: string | null;
  /** When set, the only endpoint tried; the descriptor's are ignored. */
  overrideEndpoint?: string | null;
  /** Told about every refused endpoint. */
  onRejected?: SocketEndpointRejectionReporter;
}

/** Host of a socket URL without its port, or null when unparseable. */
export function socketEndpointHost(endpoint: string | null): string | null {
  if (!endpoint) return null;
  try {
    return new URL(endpoint).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * An advertised endpoint, or null when it is refused (reported and skipped so
 * the caller moves on to the next fallback entry).
 */
function acceptEndpoint(
  endpoint: string,
  allowlist: readonly string[],
  onRejected: SocketEndpointRejectionReporter | undefined,
): string | null {
  let parsed: URL;
  try {
    parsed = new URL(endpoint);
  } catch {
    onRejected?.('malformed', null);
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (!SOCKET_SCHEMES.includes(parsed.protocol)) {
    onRejected?.('unsupported-scheme', host);
    return null;
  }
  if (parsed.search || parsed.hash) {
    // The ticket is appended here, so an endpoint that already carries a query
    // string is either malformed or smuggling per-connection state.
    onRejected?.('query-string', host);
    return null;
  }
  if (!allowlist.includes(host)) {
    onRejected?.('not-allowlisted', host);
    return null;
  }
  return endpoint;
}

/** The response body as a descriptor, or null when it carries no ticket. */
export function parseConnectionDescriptor(
  payload: unknown,
): WebSocketConnectionDescriptor | null {
  if (!payload || typeof payload !== 'object') return null;
  const body = payload as Record<string, unknown>;
  const ticket = body['ticket'];
  if (typeof ticket !== 'string' || ticket === '') return null;

  const descriptor: WebSocketConnectionDescriptor = { ticket };
  const expiresIn = body['expires_in'];
  if (
    typeof expiresIn === 'number' &&
    Number.isFinite(expiresIn) &&
    expiresIn > 0
  ) {
    descriptor.expires_in = expiresIn;
  }
  const url = body['url'];
  if (typeof url === 'string' && url !== '') {
    descriptor.url = url;
  }
  const fallback = body['fallback'];
  if (Array.isArray(fallback)) {
    descriptor.fallback = fallback.filter(
      (entry): entry is components['schemas']['WebSocketFallbackConnection'] =>
        !!entry &&
        typeof entry === 'object' &&
        typeof (entry as Record<string, unknown>)['url'] === 'string',
    );
  }
  return descriptor;
}

/**
 * The connect plan for a `ws/ticket` response, or null when it carries no
 * usable ticket or no endpoint to try.
 *
 * Endpoint order is the descriptor's: `url` first, then `fallback` as sent,
 * minus every entry the allowlist refuses. When nothing survives — an older
 * server, a descriptor the server could not produce, or one whose hosts are
 * all refused — the configured endpoint is the single candidate, which is
 * pre-descriptor behaviour.
 */
export function buildSocketConnectionPlan(
  payload: unknown,
  options: SocketConnectionPlanOptions,
): SocketConnectionPlan | null {
  const descriptor = parseConnectionDescriptor(payload);
  if (!descriptor) return null;

  const endpoints: string[] = [];
  if (options.overrideEndpoint) {
    endpoints.push(options.overrideEndpoint);
  } else {
    const advertised = [
      ...(descriptor.url ? [descriptor.url] : []),
      ...(descriptor.fallback ?? []).map((entry) => entry.url),
    ];
    for (const candidate of advertised) {
      const accepted = acceptEndpoint(
        candidate,
        options.allowlist,
        options.onRejected,
      );
      if (accepted && !endpoints.includes(accepted)) endpoints.push(accepted);
    }
  }

  if (endpoints.length === 0) {
    if (!options.configuredEndpoint) return null;
    endpoints.push(options.configuredEndpoint);
  }

  return {
    ticket: descriptor.ticket,
    ticketLifetimeMs:
      descriptor.expires_in === undefined ? null : descriptor.expires_in * 1000,
    endpoints,
  };
}

/** `<endpoint>?ticket=<ticket>` — the only place a credential enters a socket URL. */
export function socketUrlWithTicket(endpoint: string, ticket: string): string {
  return `${endpoint}?${TICKET_QUERY_PARAMETER}=${encodeURIComponent(ticket)}`;
}
